// /schemas/auth.schema.js
import { z } from "zod";

/** ————— Common Threat Patterns ————— */
const INVISIBLE_RE = /[\u200B-\u200D\u2060\uFEFF]/; // zero-width
const CONTROL_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/; // control chars
const TAG_RE = /<[^>]*>/; // any HTML/XML tags
const ANGLE_RE = /[<>]/; // angle brackets
const FULLWIDTH_AT = /＠/; // full-width '@'
const EMOJI_RE = /\p{Extended_Pictographic}/u;

// Common SQLi markers (keep concise but high-signal)
const SQLI_RE = new RegExp(
  String.raw`(--|;--|;|\bUNION\b\s+\bSELECT\b|\bSELECT\b.+\bFROM\b|\bINSERT\b\s+\bINTO\b|\bUPDATE\b\s+\w+\s+\bSET\b|\bDELETE\b\s+\bFROM\b|\bDROP\b\s+\bTABLE\b|\bSLEEP\s*\(|\bBENCHMARK\s*\(|\bINFORMATION_SCHEMA\b|\bOR\b\s+1\s*=\s*1)`,
  "i"
);

// NoSQL operator payloads / query-injection hints
const NOSQL_TOKENS = [
  "$ne",
  "$gt",
  "$gte",
  "$lt",
  "$lte",
  "$in",
  "$nin",
  "$regex",
  "$where",
  "$expr",
  "$function",
  "$accumulator",
  "$lookup",
  "$project",
  "$group",
  "$match",
  "$out",
  "$merge",
];
const hasNoSqlTokens = (s) =>
  NOSQL_TOKENS.some((t) => s.toLowerCase().includes(t.toLowerCase()));
const JSONY_RE = /[{[\]}]|("|')\s*[:]\s*("|')/; // crude JSON shape hints

// repeated chars / sequences (extra hygiene)
const TRIPLE_REPEAT_RE = /(.)\1{2,}/;
const hasLongSequence = (s) => {
  const a = [...s].map((c) => c.charCodeAt(0));
  let up = 1,
    down = 1;
  for (let i = 1; i < a.length; i++) {
    if (a[i] === a[i - 1] + 1) {
      up++;
      down = 1;
    } else if (a[i] === a[i - 1] - 1) {
      down++;
      up = 1;
    } else {
      up = down = 1;
    }
    if (up >= 4 || down >= 4) return true;
  }
  return false;
};

const safeText = (label, { allowEmoji = false } = {}) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .refine(
      (v) => !INVISIBLE_RE.test(v),
      `${label} contains invisible characters`
    )
    .refine((v) => !CONTROL_RE.test(v), `${label} contains control characters`)
    .refine((v) => !TAG_RE.test(v), `${label} cannot contain HTML tags`)
    .refine((v) => !ANGLE_RE.test(v), `${label} cannot contain < or >`)
    .refine(
      (v) => !SQLI_RE.test(v),
      `${label} contains disallowed SQL keywords/patterns`
    )
    .refine(
      (v) => !hasNoSqlTokens(v),
      `${label} contains disallowed NoSQL operators`
    )
    .refine(
      (v) => !JSONY_RE.test(v),
      `${label} must be plain text (no JSON-like payloads)`
    )
    .refine(
      (v) => allowEmoji || !EMOJI_RE.test(v),
      `${label} cannot contain emoji`
    );

/** ————— Specific field rules ————— */
const DISPOSABLE_DOMAINS = new Set([
  "mailinator.com",
  "10minutemail.com",
  "tempmail.com",
  "temp-mail.org",
  "guerrillamail.com",
  "yopmail.com",
  "1secmail.com",
  "trashmail.com",
  "getnada.com",
  "sharklasers.com",
]);

const emailSchema = safeText("Email")
  .max(254, "Email too long")
  .refine((v) => !/\s/.test(v), "Email cannot contain spaces")
  .refine(
    (v) => !FULLWIDTH_AT.test(v),
    "Email contains invalid full-width characters"
  )
  .email("Invalid email address")
  .transform((v) => v.toLowerCase())
  .refine((v) => {
    const domain = v.split("@")[1] || "";
    return !DISPOSABLE_DOMAINS.has(domain);
  }, "Please use a non-disposable email domain");

const passwordSchema = z
  .string()
  .trim()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password too long")
  // Harden without over-blocking strong symbols:
  .refine(
    (v) => !INVISIBLE_RE.test(v),
    "Password contains invisible characters"
  )
  .refine((v) => !CONTROL_RE.test(v), "Password contains control characters")
  .refine(
    (v) => !TAG_RE.test(v) && !ANGLE_RE.test(v),
    "Password cannot contain HTML/tags"
  )
  // Don’t block arbitrary symbols (users need them), but avoid obvious patterns:
  //   .refine(v => /[A-Z]/.test(v), "Password must include an uppercase letter")
  .refine((v) => /[a-z]/.test(v), "Password must include a lowercase letter")
  //   .refine(v => /\d/.test(v),    "Password must include a number")
  //   .refine(v => /[^A-Za-z0-9]/.test(v), "Password must include a symbol")
  .refine(
    (v) => !TRIPLE_REPEAT_RE.test(v),
    "Avoid repeating the same character 3+ times"
  );
// .refine(v => !hasLongSequence(v), "Avoid long sequences like abcd or 1234");

export const loginSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
  })
  // .superRefine((data, ctx) => {
  //   // Cross-field: prevent passwords containing email local-part
  //   const local = (data.email.split("@")[0] || "").toLowerCase();
  //   const p = data.password.toLowerCase();
  //   if (local && p.includes(local)) {
  //     ctx.addIssue({
  //       code: z.ZodIssueCode.custom,
  //       path: ["password"],
  //       message: "Password must not contain your email username",
  //     });
  //   }
  // });

// ✅ Forgot Password Schema
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

// ✅ Reset Password Schema
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, "Reset token is required"),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });
